/**
 * EditBrandSpaceModal
 *
 * Edit a single brand space (Team) - mirrors the Agency Command & Control
 * edit workspace modal, adapted for the Portfolio page.
 *
 * Editable fields: name, logo upload, icon picker, color picker, active toggle.
 * Carte Blanche is edited inline in the drawer, not here.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Toggle } from '@/components/ui/Toggle';
import { IconPicker } from '@/components/ui/IconPicker';
import { ColorPicker } from '@/components/ui/ColorPicker';
import { CancelButton } from '@/components/ui/CancelButton';
import { Tooltip } from '@/components/ui/Tooltip';
import { showToast } from '@/components/ui/Toast';
import { useUnsavedGuard } from '@/hooks/useUnsavedGuard';
import * as agencyService from '@/services/agency.service';
import type { PortfolioClient } from '@/data/portfolio.types';
import { teamQueryKeys } from '@/lib/queryKeys';
import { useEffectiveTeamId } from '@/hooks/useEffectiveTeamId';
import { parseBrandSpaceIssue, type BrandSpaceIssue } from '@/utils/brandSpaceErrors';
import { BrandSpaceIssueNotice } from '@/components/portfolio/BrandSpaceIssueNotice';

interface EditBrandSpaceModalProps {
  client: PortfolioClient | null;
  onClose: () => void;
}

export const EditBrandSpaceModal = ({ client, onClose }: EditBrandSpaceModalProps) => {
  const { t } = useTranslation('agency');
  const queryClient = useQueryClient();
  const { teamId: effectiveTeamId } = useEffectiveTeamId();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState('');
  const [icon, setIcon] = useState<string | null>(null);
  const [color, setColor] = useState<string | null>(null);
  const [logoUrl, setLogoUrl] = useState<string>('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [issue, setIssue] = useState<BrandSpaceIssue | null>(null);

  // ── Seed local state when a new client is opened ──
  useEffect(() => {
    if (!client) return;
    setName(client.name);
    setIcon(client.icon ?? null);
    setColor(client.customColor ?? null);
    setLogoUrl(client.logoUrl ?? '');
    setLogoPreview(client.logoUrl ?? null);
    setLogoFile(null);
    setIssue(null);
  }, [client]);

  // ── Fetch current active status - API is canonical ──
  const { data: statusData } = useQuery({
    queryKey: ['agency', 'workspace', client?.id, 'status'],
    queryFn: () => agencyService.getWorkspaceStatus(client!.id),
    enabled: client !== null,
    staleTime: 0,
  });

  useEffect(() => {
    if (statusData) setIsActive(statusData.isActive);
  }, [statusData]);

  // ── Unsaved-work guard: dirty while open and any editable field diverges
  // from the loaded client / status ──
  useUnsavedGuard(
    client !== null &&
      (name.trim() !== client.name ||
        (icon ?? null) !== (client.icon ?? null) ||
        (color ?? null) !== (client.customColor ?? null) ||
        logoFile !== null ||
        logoUrl !== (client.logoUrl ?? '') ||
        (statusData !== undefined && isActive !== statusData.isActive)),
    'edit-brand-space'
  );

  // ── Mutations ──
  const updateMutation = useMutation({
    mutationFn: (
      data: { name?: string; logo?: string | null; icon?: string | null; color?: string | null },
    ) => agencyService.updateWorkspace(client!.id, data),
  });

  const statusMutation = useMutation({
    mutationFn: (nextActive: boolean) =>
      agencyService.updateWorkspaceStatus(client!.id, nextActive),
  });

  const refreshBrandData = () => {
    if (!client) return;
    void queryClient.invalidateQueries({ queryKey: ['agency', 'portfolio'] });
    void queryClient.invalidateQueries({ queryKey: ['agency', 'workspace', client.id, 'status'] });
    void queryClient.invalidateQueries({ queryKey: teamQueryKeys.package.team(effectiveTeamId) });
  };

  // ── Reactivate a paused brand right away (the friendly path; Save also works) ──
  const handleReactivate = async () => {
    if (!client) return;
    setIssue(null);
    try {
      await statusMutation.mutateAsync(true);
      setIsActive(true);
      refreshBrandData();
      showToast(t('portfolio_edit_brand_saved', { defaultValue: 'Brand updated' }), 'success');
    } catch (err) {
      const brandIssue = parseBrandSpaceIssue(err);
      if (brandIssue) {
        setIssue(brandIssue);
        return;
      }
      const msg = err instanceof Error ? err.message : 'Unknown error';
      showToast(
        t('portfolio_edit_brand_error', { defaultValue: 'Could not save: {{msg}}', msg }),
        'error',
      );
    }
  };

  // ── Logo helpers ──
  const handleLogoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      showToast(t('agency_cc_error_select_image', { defaultValue: 'Please select an image file' }), 'error');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showToast(t('agency_cc_error_file_size', { defaultValue: 'File size must be under 5MB' }), 'error');
      return;
    }
    setLogoFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setLogoPreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    setLogoUrl('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ── Submit ──
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!client) return;
    try {
      let nextLogoUrl = logoUrl;
      if (logoFile) {
        setIsUploadingLogo(true);
        try {
          const result = await agencyService.uploadWorkspaceLogo(client.id, logoFile);
          nextLogoUrl = result.logoUrl;
        } finally {
          setIsUploadingLogo(false);
        }
      }

      const nameChanged = name.trim() !== client.name;
      const iconChanged = (icon ?? null) !== (client.icon ?? null);
      const colorChanged = (color ?? null) !== (client.customColor ?? null);
      const logoChanged = logoFile !== null || logoUrl !== (client.logoUrl ?? '');

      if (nameChanged || iconChanged || colorChanged || logoChanged) {
        await updateMutation.mutateAsync({
          ...(nameChanged && { name: name.trim() }),
          ...(iconChanged && { icon }),
          ...(colorChanged && { color }),
          ...(logoChanged && { logo: nextLogoUrl || null }),
        });
      }

      if (statusData && isActive !== statusData.isActive) {
        await statusMutation.mutateAsync(isActive);
      }

      refreshBrandData();

      showToast(
        t('portfolio_edit_brand_saved', { defaultValue: 'Brand updated' }),
        'success',
      );
      onClose();
    } catch (err) {
      // Brand Space limits (full / paused by Plan / cooldown) get an inline explanation + upsell,
      // not a raw error toast. Other fields may already be saved, so refresh and keep the modal open.
      const brandIssue = parseBrandSpaceIssue(err);
      if (brandIssue) {
        setIssue(brandIssue);
        setIsActive(statusData?.isActive ?? false);
        refreshBrandData();
        return;
      }
      const msg = err instanceof Error ? err.message : 'Unknown error';
      showToast(
        t('portfolio_edit_brand_error', { defaultValue: 'Could not save: {{msg}}', msg }),
        'error',
      );
    }
  };

  const isPaused = statusData !== undefined && !statusData.isActive;
  const isHiddenByPlan = client?.hiddenByPlan === true || issue?.kind === 'hiddenByPlan';
  // Cooldown known up front from the portfolio payload (no need to wait for the 409)
  const cooldownUntil = client?.reactivationAvailableAt ?? null;
  const isCoolingDown = cooldownUntil !== null && new Date(cooldownUntil).getTime() > Date.now();
  const noticeIssue: BrandSpaceIssue | null =
    issue ?? (isHiddenByPlan ? { kind: 'hiddenByPlan' } : isCoolingDown && isPaused ? { kind: 'cooldown', availableAt: cooldownUntil } : null);

  const isSaving = isUploadingLogo || updateMutation.isPending || statusMutation.isPending;

  return (
    <Modal
      open={client !== null}
      onOpenChange={(open) => !open && !isSaving && onClose()}
      title={t('portfolio_edit_brand_title', {
        defaultValue: 'Edit {{name}}',
        name: client?.name ?? '',
      })}
      size="md"
      showCloseButton={false}
    >
      <form onSubmit={handleSave} className="space-y-5">
        {/* Name */}
        <Input
          label={t('portfolio_edit_brand_name', { defaultValue: 'Brand name' })}
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />

        {/* Logo Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('agency_cc_workspace_logo', { defaultValue: 'Logo' })}
          </label>
          <div className="flex items-start gap-4">
            <div className="relative">
              <div className="w-20 h-20 rounded-xl bg-gray-50 flex items-center justify-center overflow-hidden border-2 border-dashed border-gray-200">
                {logoPreview ? (
                  <img src={logoPreview} alt={t('agency_cc_workspace_logo')} className="w-full h-full object-cover" />
                ) : (
                  <i className="hm hm-image text-gray-300" style={{ fontSize: 28 }} aria-hidden="true" />
                )}
              </div>
              {logoPreview && (
                <Tooltip content={t('agency_cc_remove_logo', { defaultValue: 'Remove logo' })}>
                  <button
                    type="button"
                    onClick={handleRemoveLogo}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-coral-500 hover:bg-coral-600 text-coral-contrast rounded-full flex items-center justify-center shadow-lg transition-colors"
                    aria-label={t('agency_cc_remove_logo')}
                  >
                    <i className="hm hm-cross" style={{ fontSize: 10 }} aria-hidden="true" />
                  </button>
                </Tooltip>
              )}
            </div>
            <div className="flex-1">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleLogoFileChange}
                className="hidden"
                id="edit-logo-upload"
              />
              <label
                htmlFor="edit-logo-upload"
                className="flex flex-col items-center justify-center px-4 py-4 border-2 border-dashed border-gray-200 rounded-xl cursor-pointer hover:border-primary-400 hover:bg-soft-mint/50 transition-all duration-200 group"
              >
                <i className="hm hm-upload text-gray-400 group-hover:text-dark-teal mb-1" style={{ fontSize: 22 }} aria-hidden="true" />
                <span className="text-sm font-medium text-gray-600 group-hover:text-dark-teal">
                  {logoFile
                    ? t('agency_cc_change_image', { defaultValue: 'Change image' })
                    : t('agency_cc_upload_image', { defaultValue: 'Upload image' })}
                </span>
                <span className="text-xs text-gray-400 mt-0.5">
                  {t('agency_cc_image_format', { defaultValue: 'PNG, JPG · up to 5MB' })}
                </span>
              </label>
              {logoFile && (
                <p className="mt-2 text-xs text-gray-500 truncate">
                  {t('agency_cc_selected', { defaultValue: 'Selected' })}: {logoFile.name}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Icon */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('agency_cc_label_workspace_icon', { defaultValue: 'Brand icon' })}
          </label>
          <IconPicker value={icon} onChange={setIcon} />
        </div>

        {/* Color */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('agency_cc_label_workspace_color', { defaultValue: 'Brand color' })}
          </label>
          {/* Default to the brand's primary palette (matches unconfigured tenant theme).
              Custom whitelabel themes will still resolve to their own palette via CSS vars. */}
          <ColorPicker value={color || '#69C7C1'} onChange={setColor} />
          {color && (
            <button
              type="button"
              onClick={() => setColor(null)}
              className="mt-1 text-xs text-gray-400 hover:text-gray-600 transition-colors"
            >
              {t('agency_cc_label_clear_color', { defaultValue: 'Clear color' })}
            </button>
          )}
        </div>

        {/* Active toggle */}
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
          <div>
            <div className="font-medium text-gray-900 flex items-center gap-2 flex-wrap">
              {t('agency_cc_active_status', { defaultValue: 'Active status' })}
              {isPaused && (
                <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-gray-600">
                  <i className="hm hm-pause" style={{ fontSize: 10 }} aria-hidden="true" />
                  {isHiddenByPlan
                    ? t('brand_space_badge_paused_by_plan')
                    : t('brand_space_badge_paused')}
                </span>
              )}
            </div>
            <div className="text-sm text-gray-500">
              {isActive
                ? t('agency_cc_workspace_active', { defaultValue: 'Team can post and be managed' })
                : t('agency_cc_workspace_inactive', { defaultValue: 'Brand is inactive, posts are paused' })}
            </div>
          </div>
          {isPaused && !isHiddenByPlan ? (
            <Button type="button" variant="outline" size="sm" onClick={() => void handleReactivate()} loading={statusMutation.isPending} disabled={isCoolingDown}>
              {t('brand_space_reactivate')}
            </Button>
          ) : (
            <Toggle
              ariaLabel={t('agency_cc_active_status', { defaultValue: 'Active status' })}
              checked={isActive}
              onChange={(next) => {
                setIssue(null);
                setIsActive(next);
              }}
              disabled={isHiddenByPlan}
            />
          )}
        </div>
        {isPaused && !isActive && !isHiddenByPlan && (
          <p className="text-sm text-gray-500 -mt-2">{t('brand_space_toggle_hint_paused')}</p>
        )}
        {noticeIssue && <BrandSpaceIssueNotice issue={noticeIssue} action="reactivate" onNavigate={onClose} className="-mt-2" />}
        {!isActive && !isHiddenByPlan && (
          <p className="text-sm text-amber-700 bg-amber-50 rounded-xl px-4 py-3 -mt-2">
            {t('agency_cc_workspace_inactive_warning', {
              defaultValue:
                "Scheduled posts won't be published while the brand is inactive. Posts that come due during that time are marked as missed and can be rescheduled.",
            })}
          </p>
        )}

        {/* Actions */}
        <div className="flex gap-2 pt-2 justify-end">
          <CancelButton onClick={onClose} tooltip={t('agency_cc_cancel', { defaultValue: 'Cancel' })} />
          <Button type="submit" loading={isSaving}>
            {isUploadingLogo
              ? t('agency_cc_uploading', { defaultValue: 'Uploading…' })
              : t('agency_cc_save_settings', { defaultValue: 'Save changes' })}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default EditBrandSpaceModal;
